// src/pages/Dashboard/Admin/DetailsAdherents/components/AdherentFiltersCard.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FaBirthdayCake,
  FaCalendarAlt,
  FaChevronDown,
  FaEnvelope,
  FaFilter,
  FaIdCard,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaSearch,
  FaSyncAlt,
  FaUsers,
} from "react-icons/fa";

import {
  computeAge,
  getAdherentStatusClass,
  getFullName,
  getGroupsFromAdherent,
  normalizeText,
  toAbsoluteUrl,
} from "../utils/adherentDashboardUtils";

const DEFAULT_AVATAR = "/img/user_avatar.jpg";

function getAdherentPhoto(adherent) {
  const photo = toAbsoluteUrl(adherent?.photo_url);

  if (photo) return photo;

  if (adherent?.genre === "Féminin") {
    return "/img/judokate_avatar.png";
  }

  return DEFAULT_AVATAR;
}

function SmallInfo({ icon, children }) {
  if (!children) return null;

  return (
    <span className="inline-flex min-w-0 items-center gap-1.5 rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700">
      <span className="shrink-0 text-slate-400">{icon}</span>
      <span className="truncate">{children}</span>
    </span>
  );
}

function AdherentCombobox({
  adherents = [],
  selectedAdherentId,
  selectAdherent,
  loading,
}) {
  const wrapperRef = useRef(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const selected = useMemo(
    () =>
      adherents.find(
        (adherent) => String(adherent.id) === String(selectedAdherentId),
      ) || null,
    [adherents, selectedAdherentId],
  );

  useEffect(() => {
    if (!open) {
      setQuery(selected ? getFullName(selected) : "");
    }
  }, [selected, open]);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const results = useMemo(() => {
    const q = normalizeText(query);

    if (!q) return adherents.slice(0, 80);

    const tokens = q.split(/\s+/).filter(Boolean);

    return adherents
      .filter((adherent) => {
        const groups = getGroupsFromAdherent(adherent);

        const searchable = normalizeText(
          [
            adherent?.id,
            adherent?.nom,
            adherent?.prenom,
            adherent?.email,
            adherent?.email_secondaire,
            adherent?.tel,
            adherent?.tel_secondaire,
            adherent?.licence_number,
            adherent?.commune,
            adherent?.cp,
            adherent?.adresse,
            adherent?.saison_active,
            adherent?.statut_inscription,
            adherent?.representant_legal,
            ...groups.map((group) => group?.nom),
          ]
            .filter(Boolean)
            .join(" "),
        );

        return tokens.every((token) => searchable.includes(token));
      })
      .slice(0, 80);
  }, [adherents, query]);

  const choose = (adherent) => {
    selectAdherent(adherent?.id || "");
    setQuery(adherent ? getFullName(adherent) : "");
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="mb-1 flex items-center justify-between gap-2">
        <label className="block text-sm font-bold text-slate-700 dark:text-slate-200">
          Sélectionner un adhérent
        </label>

        <span className="text-xs font-bold text-slate-400">
          {adherents.length} résultat{adherents.length > 1 ? "s" : ""}
        </span>
      </div>

      <div className="relative">
        <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />

        <input
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          disabled={loading}
          placeholder="Nom, prénom, email, téléphone, licence, groupe..."
          className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-10 pr-10 text-sm font-medium text-slate-900 outline-none transition focus:ring-2 focus:ring-indigo-400 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
        />

        <FaChevronDown className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
      </div>

      {selected && !open ? (
        <div className="mt-2 flex items-center gap-3 rounded-2xl border border-indigo-100 bg-indigo-50/60 p-3 dark:border-indigo-500/20 dark:bg-indigo-500/10">
          <img
            src={getAdherentPhoto(selected)}
            alt={getFullName(selected)}
            className="h-11 w-11 shrink-0 rounded-2xl object-cover ring-1 ring-indigo-100 dark:ring-indigo-500/20"
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = DEFAULT_AVATAR;
            }}
          />

          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-bold text-slate-900 dark:text-white">
              {(selected?.nom || "").toUpperCase()} {selected?.prenom || ""}
            </p>

            <p className="truncate text-xs font-medium text-slate-500 dark:text-slate-400">
              {selected?.email || "Email non renseigné"}
              {selected?.licence_number
                ? ` · Licence ${selected.licence_number}`
                : ""}
            </p>
          </div>

          <span
            className={`shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ${getAdherentStatusClass(
              selected?.statut_inscription,
            )}`}
          >
            {selected?.statut_inscription || "—"}
          </span>
        </div>
      ) : null}

      {open ? (
        <div className="absolute z-[80] mt-2 max-h-[34rem] w-full overflow-auto rounded-[24px] border border-slate-200 bg-white p-2 shadow-2xl dark:border-slate-700 dark:bg-slate-950">
          <div className="sticky top-0 z-10 mb-2 rounded-2xl border border-slate-200 bg-white/95 px-3 py-2 text-xs font-bold text-slate-500 backdrop-blur dark:border-slate-800 dark:bg-slate-950/95 dark:text-slate-400">
            {results.length} adhérent{results.length > 1 ? "s" : ""} affiché
            {results.length > 1 ? "s" : ""}
            {adherents.length > results.length
              ? ` sur ${adherents.length}`
              : ""}
          </div>

          {results.length === 0 ? (
            <div className="rounded-2xl bg-slate-50 p-4 text-sm text-slate-500 dark:bg-slate-900 dark:text-slate-400">
              Aucun adhérent trouvé.
            </div>
          ) : (
            <div className="space-y-2">
              {results.map((adherent) => {
                const groups = getGroupsFromAdherent(adherent);
                const active =
                  String(adherent.id) === String(selectedAdherentId);
                const age = computeAge(adherent?.date_naissance);

                return (
                  <button
                    type="button"
                    key={adherent.id}
                    onClick={() => choose(adherent)}
                    className={[
                      "flex w-full gap-3 rounded-2xl border p-3 text-left transition",
                      active
                        ? "border-indigo-300 bg-indigo-50 ring-2 ring-indigo-100 dark:border-indigo-500/40 dark:bg-indigo-500/10 dark:ring-indigo-500/10"
                        : "border-slate-200 bg-white hover:border-indigo-200 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950 dark:hover:border-indigo-500/30 dark:hover:bg-slate-900",
                    ].join(" ")}
                  >
                    <img
                      src={getAdherentPhoto(adherent)}
                      alt={getFullName(adherent)}
                      className="h-16 w-16 shrink-0 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                      onError={(event) => {
                        event.currentTarget.onerror = null;
                        event.currentTarget.src = DEFAULT_AVATAR;
                      }}
                    />

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-bold text-slate-900 dark:text-white">
                            {(adherent?.nom || "").toUpperCase()}{" "}
                            {adherent?.prenom || ""}
                          </p>

                          <div className="mt-1 flex flex-wrap items-center gap-1.5">
                            <span
                              className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-bold ring-1 ${getAdherentStatusClass(
                                adherent?.statut_inscription,
                              )}`}
                            >
                              {adherent?.statut_inscription || "Statut inconnu"}
                            </span>

                            {adherent?.saison_active ? (
                              <SmallInfo icon={<FaCalendarAlt />}>
                                {adherent.saison_active}
                              </SmallInfo>
                            ) : null}

                            {adherent?.licence_number ? (
                              <SmallInfo icon={<FaIdCard />}>
                                {adherent.licence_number}
                              </SmallInfo>
                            ) : null}

                            {age !== null ? (
                              <SmallInfo icon={<FaBirthdayCake />}>
                                {age} ans
                              </SmallInfo>
                            ) : null}
                          </div>
                        </div>

                        <span className="shrink-0 rounded-full bg-white px-2 py-1 text-[11px] font-bold text-slate-500 ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
                          #{adherent.id}
                        </span>
                      </div>

                      <div className="mt-2 grid gap-1.5 text-xs text-slate-500 dark:text-slate-400 sm:grid-cols-2">
                        <div className="flex min-w-0 items-center gap-2">
                          <FaEnvelope className="shrink-0 text-slate-400" />
                          <span className="truncate">
                            {adherent?.email || "Email non renseigné"}
                          </span>
                        </div>

                        <div className="flex min-w-0 items-center gap-2">
                          <FaPhoneAlt className="shrink-0 text-slate-400" />
                          <span className="truncate">
                            {adherent?.tel || "Téléphone non renseigné"}
                          </span>
                        </div>

                        <div className="flex min-w-0 items-center gap-2 sm:col-span-2">
                          <FaMapMarkerAlt className="shrink-0 text-slate-400" />
                          <span className="truncate">
                            {[
                              adherent?.adresse,
                              adherent?.cp,
                              adherent?.commune,
                            ]
                              .filter(Boolean)
                              .join(" ") || "Adresse non renseignée"}
                          </span>
                        </div>
                      </div>

                      {groups.length > 0 ? (
                        <div className="mt-2 flex flex-wrap gap-1">
                          <span className="inline-flex items-center gap-1 rounded-full bg-slate-900 px-2 py-0.5 text-[11px] font-bold text-white dark:bg-white dark:text-slate-900">
                            <FaUsers />
                            Groupes
                          </span>

                          {groups.slice(0, 4).map((group) => (
                            <span
                              key={`${adherent.id}-${group.id || group.nom}`}
                              className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700"
                            >
                              {group.nom}
                            </span>
                          ))}

                          {groups.length > 4 ? (
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700">
                              +{groups.length - 4}
                            </span>
                          ) : null}
                        </div>
                      ) : (
                        <div className="mt-2 text-xs font-semibold text-slate-400">
                          Aucun groupe renseigné
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

export default function AdherentFiltersCard({
  seasons,
  seasonFilter,
  setSeasonFilter,
  dojos,
  selectedDojo,
  handleDojoChange,
  groups,
  selectedGroup,
  setSelectedGroup,
  adherents,
  selectedAdherentId,
  selectAdherent,
  loading,
  loadingDetails,
  refreshCurrentAdherent,
  totalFiltered,
  errorMsg,
}) {
  return (
    <section className="overflow-visible rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Recherche
            </p>

            <h2 className="mt-1 flex items-center gap-2 text-xl font-bold text-slate-900 dark:text-white">
              <FaFilter className="text-indigo-600 dark:text-indigo-300" />
              Filtres & sélection
            </h2>
          </div>

          <button
            type="button"
            onClick={refreshCurrentAdherent}
            disabled={loading || loadingDetails || !selectedAdherentId}
            className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            <FaSyncAlt
              className={loading || loadingDetails ? "animate-spin" : ""}
            />
            Actualiser
          </button>
        </div>

        <div className="grid gap-3 lg:grid-cols-12">
          <div className="lg:col-span-2">
            <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
              Saison
            </label>

            <select
              value={seasonFilter}
              onChange={(event) => setSeasonFilter(event.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
            >
              <option value="">Toutes saisons</option>
              {seasons.map((season) => (
                <option key={season} value={season}>
                  Saison {season}
                </option>
              ))}
            </select>
          </div>

          {dojos.length > 1 ? (
            <div className="lg:col-span-2">
              <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
                Dojo
              </label>

              <select
                value={selectedDojo}
                onChange={handleDojoChange}
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
              >
                <option value="">Tous les dojos</option>
                {dojos.map((dojo) => (
                  <option key={dojo.id} value={dojo.id}>
                    {dojo.dojo_nom || dojo.nom || `Dojo #${dojo.id}`}
                  </option>
                ))}
              </select>
            </div>
          ) : null}

          <div className={dojos.length > 1 ? "lg:col-span-3" : "lg:col-span-3"}>
            <label className="mb-1 block text-sm font-bold text-slate-700 dark:text-slate-200">
              Groupe
            </label>

            <select
              value={selectedGroup}
              onChange={(event) => setSelectedGroup(event.target.value)}
              className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200"
            >
              <option value="">Tous les groupes</option>
              {groups.map((group) => (
                <option key={group.id || group.nom} value={group.nom}>
                  {group.nom}
                </option>
              ))}
            </select>
          </div>

          <div className={dojos.length > 1 ? "lg:col-span-5" : "lg:col-span-7"}>
            <AdherentCombobox
              adherents={adherents}
              selectedAdherentId={selectedAdherentId}
              selectAdherent={selectAdherent}
              loading={loading}
            />
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2 text-xs text-slate-600 dark:text-slate-300">
          <span className="rounded-full bg-slate-100 px-3 py-1 font-bold dark:bg-slate-800">
            {totalFiltered} adhérent(s) filtré(s)
          </span>

          {selectedAdherentId ? (
            <span className="rounded-full bg-indigo-50 px-3 py-1 font-bold text-indigo-700 ring-1 ring-indigo-100 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20">
              Adhérent sélectionné #{selectedAdherentId}
            </span>
          ) : null}
        </div>

        {errorMsg ? (
          <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300">
            {errorMsg}
          </div>
        ) : null}
      </div>
    </section>
  );
}
